<template>
  <div class="folder_container">
    <div v-if="fetchingWorkflow" class="folder_header">
      <div v-for="i in 3" :key="`l-${i}`" class="header_item">
        <div class="item_title">
          <v-skeleton-loader class="skeleton" type="text" />
          <v-btn color="grey" class="action" icon disabled>
            <BaseIcon icon="$mdiDotsHorizontal" />
          </v-btn>
        </div>
        <div class="sub_title">
          <v-skeleton-loader class="skeleton" type="text" />
        </div>
        <div class="item_content">
          <v-skeleton-loader class="skeleton" type="text" />
        </div>
        <div v-if="i == 1" class="item_content">
          <v-skeleton-loader class="skeleton" type="text" />
        </div>
      </div>
    </div>
    <template v-if="fetchingWorkflow">
      <div v-if="folder && folder.state == 'CLOSED'" class="archived_alert">
        <div class="archived_alert_icon">
          <BaseIcon icon="$mdiAlertCircle" />
        </div>
        <div class="archived_alert_content">
          <div
            class="archived_info"
            v-text="$t('utils.folderArchivedMessage')"
          />
          <div class="archived_info">
            <div v-text="`${$t('utils.archiveDate')} :`" />
            <div
              class="text_strong"
              v-text="`le ${$d(new Date(folder.dateUpdated), 'veryshort')}`"
            />
          </div>
        </div>
      </div>
    </template>

    <div class="container_full">
      <div class="main_section">
        <template v-if="folder && !fetchingWorkflow">
          <!-- HEADER -->
          <div class="folder_header">
            <!-- FOLDER -->
            <div class="header_item">
              <div class="item_title">
                <span v-text="$t('utils.folder')" />
                <!-- FIRST NESTED MENU -->
                <v-menu v-model="nestedMenuModel.first" offset-y left>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      color="grey"
                      class="action"
                      icon
                      v-bind="attrs"
                      v-on="on"
                    >
                      <BaseIcon icon="$mdiDotsHorizontal" />
                    </v-btn>
                  </template>
                  <div class="menu_list">
                    <v-btn
                      x-large
                      class="menu_list_item"
                      block
                      text
                      @click="
                        $store.commit('folder/SET_MODAL_FOlDER_EDIT_NAME', true)
                      "
                    >
                      <BaseIcon
                        class="icon"
                        icon="$mdiPencilOutline"
                        small
                        left
                      />
                      <span class="label" v-text="$t('folder.editName')" />
                    </v-btn>
                    <v-btn
                      x-large
                      class="menu_list_item"
                      block
                      text
                      @click="
                        $store.commit('folder/setFolderLogsDialogOpen', true)
                      "
                    >
                      <BaseIcon
                        class="icon"
                        icon="$mdiClockOutline"
                        small
                        left
                      />
                      <span class="label" v-text="$t('folder.seeHistory')" />
                    </v-btn>
                    <!-- SECOND NESTED MENU -->
                    <template v-if="exportCheck">
                      <v-menu
                        v-model="nestedMenuModel.second"
                        :close-on-content-click="false"
                        :close-on-content-leave="false"
                        open-on-hover
                        offset-x
                        right
                      >
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-large
                            class="menu_list_item"
                            block
                            text
                            v-bind="attrs"
                            v-on="on"
                            @click.stop.prevent
                          >
                            <BaseIcon
                              class="icon"
                              icon="$mdiDownload"
                              small
                              left
                            />
                            <span class="label" v-text="$t('utils.download')" />
                          </v-btn>
                        </template>
                        <div class="menu_list">
                          <v-btn
                            x-large
                            class="menu_list_item"
                            block
                            text
                            :disabled="!exportCheck.pdf.allowed"
                            :loading="exportingType === 'PDF'"
                            @click="downloadFolder('PDF')"
                          >
                            <BaseIcon
                              class="icon"
                              icon="$mdiFileOutline"
                              small
                              left
                            />
                            <span class="label" v-text="'PDF'" />
                          </v-btn>
                          <v-btn
                            x-large
                            class="menu_list_item"
                            block
                            text
                            :disabled="disabledExportZipBtn"
                            :loading="exportingType === 'ZIP'"
                            @click="downloadFolder('ZIP')"
                          >
                            <BaseIcon
                              class="icon"
                              icon="$mdiFolderZip"
                              small
                              left
                            />
                            <span class="label" v-text="'Archive'" />
                          </v-btn>
                        </div>
                      </v-menu>
                    </template>
                    <v-btn
                      v-if="folder.state == 'OPEN'"
                      x-large
                      class="menu_list_item"
                      block
                      text
                      @click="archiveFolder()"
                    >
                      <BaseIcon class="icon" icon="$mdiArchive" small left />
                      <span class="label" v-text="$t('utils.archive')" />
                    </v-btn>
                    <v-btn
                      x-large
                      class="menu_list_item"
                      block
                      text
                      @click="
                        $store.commit('folder/SET_MODAL_DELETE_FOLDER', true)
                      "
                    >
                      <BaseIcon class="icon" icon="$mdiTrashCan" small left />
                      <span class="label" v-text="$t('folder.deleteFolder')" />
                    </v-btn>
                  </div>
                </v-menu>
              </div>
              <div class="d-flex flex-row">
                <div
                  class="pastille"
                  :style="{ backgroundColor: getValidationColor }"
                />
                <div class="sub_title" v-text="folder.name" />
              </div>

              <div class="item_content">
                <div class="item_key" v-text="`${$t('utils.creation')} : `" />
                <div
                  class="item_value"
                  v-text="$d(new Date(folder.dateCreated), 'veryshort')"
                />
              </div>
              <div class="item_content">
                <div class="item_key" v-text="`${$t('utils.lastEdit')} : `" />
                <div
                  class="item_value"
                  v-text="$d(new Date(folder.dateUpdated), 'veryshort')"
                />
              </div>
              <FolderTagList :folder-id="folderId" class="tag_list" />
              <button
                v-if="showDownloadZipBtn"
                class="download_zip_btn"
                :disabled="disabledDownloadZipBtn"
                :class="{
                  custom_tooltip: disabledDownloadZipBtn,
                }"
                @click="downloadZIPFile(exportCheck.lastZipExport)"
              >
                <BaseIcon class="icon" icon="$mdiDownload" />
                <span
                  v-text="
                    $t('utils.download_archive_generated', {
                      date: $d(
                        new Date(exportCheck.lastZipExport.dateCreated),
                        'veryshort'
                      ),
                    })
                  "
                />
                <div
                  v-if="disabledDownloadZipBtn"
                  class="tooltip_content"
                  v-text="
                    $t(
                      exportInError
                        ? 'folder.downloadFolderError'
                        : 'folder.downloadFolderInProgress'
                    )
                  "
                />
              </button>
              <template v-if="folder.state == 'CLOSED'">
                <div class="item_content_right">
                  <button
                    class="unarchive_btn"
                    :disabled="updatingFolderState"
                    @click="unarchiveFolder()"
                  >
                    <BaseIcon class="icon" icon="$mdiFolderRefreshOutline" />
                    <span v-text="$t('utils.unarchive')" />
                  </button>
                </div>
                <div
                  v-if="remainingDeletionDays"
                  class="alert_message"
                  v-text="remainingDeletionDays"
                />
              </template>
              <div v-else class="item_footer_action">
                <button
                  v-if="!watchers.items.length"
                  class="footer_btn"
                  @click="openAssignWatcherDialog = true"
                >
                  <span v-text="$t('utils.add_observer')" />
                </button>
                <button
                  v-else
                  class="followers"
                  @click="openAssignWatcherDialog = true"
                >
                  <div
                    v-for="(user, index) in watchers.items.slice(0, 3)"
                    :key="`u${index}`"
                    class="avatar_container"
                  >
                    <div v-text="user.firstChar" />
                  </div>
                  <div
                    v-if="watchers.items.length > 3"
                    class="dots"
                    v-text="'...'"
                  />
                </button>
                <button
                  :disabled="submittingFollower"
                  class="footer_btn"
                  @click="handleFollow()"
                >
                  <BaseIcon class="icon" :icon="watchingBtn.icon" />
                  <span v-text="watchingBtn.label" />
                </button>
              </div>
              <!-- to change -->
              <div v-if="folder.collectionInProgress">
                <div class="action_container">
                  <div class="btn_1">
                    <BaseButton
                      block
                      @click="displayRelaunchFolderDialog = true"
                    >
                      <span v-text="$t('utils.notify')" />
                    </BaseButton>
                  </div>
                  <div v-if="lastNotification" class="btn_2">
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on">
                          <BaseButton
                            block
                            color="#707080"
                            text
                            @click="displayNotificationHistoryDialog = true"
                          >
                            <BaseIcon small icon="$mdiClockTimeFourOutline" />
                          </BaseButton>
                        </div>
                      </template>
                      <span>{{ $t("utils.showRelaunchHistory") }}</span>
                    </v-tooltip>
                  </div>
                </div>
                <!-- notification content -->
                <template v-if="lastNotification">
                  <div class="item_content">
                    <div
                      class="item_key"
                      v-text="`${$t('utils.lastNotification')} : `"
                    />
                    <div
                      class="item_value"
                      v-text="
                        $d(new Date(lastNotification.dateCreated), 'detailed')
                      "
                    />
                  </div>
                </template>
              </div>

              <!-- end change -->
            </div>
            <!-- CONTACT -->
            <div class="header_item">
              <div class="item_title">
                <span v-text="$tc('utils.contact', 1)" />
                <v-menu v-if="folder.client && hasClient" offset-y left>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      color="grey"
                      class="action"
                      icon
                      v-bind="attrs"
                      v-on="on"
                    >
                      <BaseIcon icon="$mdiDotsHorizontal" />
                    </v-btn>
                  </template>
                  <div class="menu_list">
                    <v-btn
                      x-large
                      class="menu_list_item"
                      block
                      text
                      @click="
                        $router.push({
                          name: 'clientDetail',
                          params: {
                            organizationId: $route.params.organizationId,
                            clientId: folder.client.id,
                          },
                        })
                      "
                    >
                      <BaseIcon class="icon" icon="$mdiAccount" small left />
                      <span
                        class="label"
                        v-text="$t('utils.seeContactDetails')"
                      />
                    </v-btn>
                    <v-btn
                      x-large
                      class="menu_list_item"
                      block
                      text
                      @click="
                        $store.commit('folder/setFolderChangeClient', true)
                      "
                    >
                      <BaseIcon
                        class="icon"
                        icon="$mdiPencilOutline"
                        small
                        left
                      />
                      <span class="label" v-text="$t('folder.editClient')" />
                    </v-btn>
                  </div>
                </v-menu>
              </div>
              <template v-if="folder.client && hasClient">
                <div class="sub_title" v-text="folder.client.email" />
                <div v-if="folder.client.fullName" class="item_content">
                  <div class="item_key" v-text="`${$t('utils.fullname')} : `" />
                  <div class="item_value" v-text="folder.client.fullName" />
                </div>
              </template>
              <template v-else>
                <div class="sub_title" v-text="$t('utils.noAssignedContact')" />
                <div v-if="folder.state == 'OPEN'" class="item_footer_action">
                  <button
                    class="footer_btn"
                    @click="$store.commit('folder/setFolderChangeClient', true)"
                  >
                    <BaseIcon class="icon" icon="$mdiAccount" />
                    <span v-text="$t('utils.assignedContact')" />
                  </button>
                </div>
              </template>
            </div>
            <!-- WORKFLOW -->
            <div class="header_item">
              <div class="item_title">
                <span v-text="$t('utils.workflow')" />
                <v-menu offset-y left>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      color="grey"
                      class="action"
                      icon
                      v-bind="attrs"
                      v-on="on"
                    >
                      <BaseIcon icon="$mdiDotsHorizontal" />
                    </v-btn>
                  </template>
                  <div class="menu_list">
                    <v-btn
                      x-large
                      class="menu_list_item"
                      block
                      text
                      @click="
                        $store.commit(
                          'folder/SET_MODAL_FOLDER_EDIT_WORKFLOW',
                          true
                        )
                      "
                    >
                      <BaseIcon
                        class="icon"
                        icon="$mdiPencilOutline"
                        small
                        left
                      />
                      <span class="label" v-text="$t('folder.editWorkflow')" />
                    </v-btn>
                  </div>
                </v-menu>
              </div>
              <template v-if="activeWorkflow">
                <div class="sub_title" v-text="activeWorkflow.name" />
                <div v-if="folder.assign" class="item_content">
                  <div
                    class="item_key"
                    v-text="$t('utils.assignedCollaborator')"
                  />
                  <div class="item_value" v-text="assignEmail" />
                </div>
                <div class="item_footer_action">
                  <button
                    v-if="folder.state == 'OPEN'"
                    class="footer_btn"
                    @click="
                      $store.commit(
                        'folder/SET_MODAL_ASSIGN_COLLABORATOR_STATUS',
                        true
                      )
                    "
                  >
                    <BaseIcon class="icon" icon="$mdiAccount" />
                    <span v-text="$t('utils.editAssignedCollaborator')" />
                  </button>
                </div>
              </template>
              <template v-else>
                <div
                  class="sub_title"
                  v-text="$t('utils.noAssignedWorkflow')"
                />
                <div v-if="folder.state == 'OPEN'" class="item_footer_action">
                  <button
                    class="footer_btn"
                    @click="
                      $store.commit(
                        'folder/SET_MODAL_FOLDER_EDIT_WORKFLOW',
                        true
                      )
                    "
                  >
                    <BaseIcon class="icon" icon="$mdiCogs" />
                    <span v-text="$t('utils.assignWorkflow')" />
                  </button>
                </div>
              </template>
            </div>
          </div>
        </template>
        <!-- WORKFLOW -->
        <div class="folder_body">
          <FolderWokflowDetail
            v-if="activeWorkflow"
            :workflow="activeWorkflow"
            :folder-active-status-id="folder ? folder.status : NaN"
            @questionAnswered="fetchFolderExportCheck()"
          />
          <FolderRecords
            v-if="folder"
            :organization-id="organizationId"
            :enable-relaunch="enableRelaunch"
            :folder-id="folderId"
          />
        </div>
      </div>
      <div class="comment_section" :class="{ active: active }">
        <template v-if="!active">
          <div class="default_view" @click.stop="openCommentSection()">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <div class="icon_container" v-bind="attrs" v-on="on">
                  <BaseIcon icon="$mdiCommentMultiple" color="primary" />
                  <span v-if="commentsCount > 0" class="comment_badge">{{
                    commentsCount
                  }}</span>
                </div>
              </template>
              <span>{{ $t("utils.showCommentSection") }}</span>
            </v-tooltip>
          </div>
        </template>
        <template v-else>
          <div class="comment_views">
            <div class="comment_title">
              {{ $t("utils.comments") }} ({{ commentsCount }})
              <BaseIcon
                class="close_icon"
                icon="$mdiClose"
                @click.stop="closeDrawer()"
              />
            </div>
            <div ref="commentList" class="comment_list mb-5" @scroll.passive="onCommentsScroll">
              <template v-if="comments.loading">
                <v-skeleton-loader
                  v-for="n in 4"
                  :key="n"
                  class="notification_skeleton"
                  type="text"
                />
              </template>
              <template v-else>
                <v-skeleton-loader
                  v-if="loadingMoreComments"
                  class="notification_skeleton"
                  type="text"
                />
                <div
                  v-for="(comment, index) in comments.items"
                  :key="`comment-${index}`"
                  class="comment_item"
                >
                  <div class="header_item">
                    <div>
                      <BaseAvatar
                        width="38"
                        height="38"
                        min-width="auto"
                        class="user_avatar"
                      />
                    </div>

                    <div class="name_container">
                      <div class="author_name d-flex">
                        {{ comment.author.email }}
                        <BaseIcon
                          v-if="comment.author.email === user.email"
                          icon="$mdiDotsHorizontal"
                          class="ml-auto"
                          @click.stop="setMenuOpen(comment.id)"
                        />
                        <div
                          v-if="menuOpen.some((id) => id === comment.id)"
                          ref="dropdown"
                          class="dropdown"
                        >
                          <li
                            class="dropdown_btn d-flex align-center"
                            @click="
                              editSection = {
                                id: comment.id,
                                content: comment.content,
                              }
                            "
                          >
                            <BaseIcon
                              class="icon mr-2"
                              icon="$mdiPencilOutline"
                              color="#707080"
                            />{{ $t("utils.edit") }}
                          </li>
                          <li
                            class="dropdown_btn d-flex align-center"
                            @click="deleteFolderComment(comment.id)"
                          >
                            <BaseIcon
                              class="icon mr-2"
                              icon="$mdiTrashCanOutline"
                              color="#707080"
                            />{{ $t("utils.delete") }}
                          </li>
                        </div>
                      </div>
                      <span class="date_created">
                        {{
                          $d(new Date(comment.dateCreated), "detailed")
                        }}</span
                      >
                    </div>
                  </div>
                  <template v-if="editSection.id === comment.id">
                    <BaseMarkdownEditor
                      id="comment-editor-edit"
                      v-model="editSection.content"
                      :placeholder="$t('utils.edit')"
                    />
                    <div class="mt-2 d-flex">
                      <BaseButton
                        type="secondary"
                        class="ml-auto"
                        color="#707080"
                        @click="editSection = { id: null, content: null }"
                      >
                        {{ $t("utils.cancel") }}</BaseButton
                      >
                      <BaseButton @click.stop="editComment">
                        {{ $t("utils.validate") }}</BaseButton
                      >
                    </div>
                  </template>
                  <template v-else>
                    <BaseMarkdownViewer
                      :content="comment.content"
                      class="comment"
                    />
                  </template>
                </div>
              </template>
            </div>
            <div class="d-flex flex-column mt-auto">
              <BaseMarkdownEditor
                id="comment-editor"
                v-model="form.comment"
                :placeholder="$t('utils.yourComments')"
              />
              <BaseButton
                class="mt-2 ml-auto"
                :disabled="$v.form.comment.$invalid"
                :loading="isSendingComment"
                @click="sendComment"
              >
                {{ $t("utils.send") }}</BaseButton
              >
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- DIALOG -->
    <FolderEditNameDialog />
    <FolderLogsShowDialog />
    <FolderChangeOwnerDialog />
    <FolderAssignCollaboratorDialog />
    <FolderEditWorkflowDialog />
    <FolderSendDataToDPAEDialog />
    <FolderSendMailDialog />
    <FolderDeleteDialog />
    <FolderAssignWatcherDialog
      v-if="openAssignWatcherDialog"
      :organization-id="organizationId * 1"
      :folder-id="folderId * 1"
      :watchers="watchers.items"
      @close="openAssignWatcherDialog = false"
      @updateWatchers="updateWatchers"
    />
    <FolderRelaunchDialog
      v-if="displayRelaunchFolderDialog"
      @relaunch="onRelaunch"
      @close="displayRelaunchFolderDialog = false"
    />
    <FolderNotificationHistoryDialog
      v-if="displayNotificationHistoryDialog"
      :organization-id="organizationId"
      :folder-id="folderId"
      @close="displayNotificationHistoryDialog = false"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import User from "@/core/Models/User";
import FolderAPI from "@/modules/Folder/Services/folder.api";
import {
  createFolderComment,
  deleteFolderComment,
  getFolderComments,
  getFolderExportCheck,
  getFolderRemind,
  postFolderExport,
  updateFolderComment,
} from "@/modules/Folder/Services/folder.service";
import Comment from "@/core/Models/Comment";

import { EXPORT_FILE_TYPE, EXPORT_STATUS } from "@/core/Models/FolderExport";
import FolderAssignWatcherDialog from "@/modules/Folder/Components/FolderAssignWatcherDialog.vue";
import FolderWokflowDetail from "@/modules/Folder/Components/FolderWokflowDetail";
import FolderEditNameDialog from "@/modules/Folder/Components/FolderEditNameDialog.vue";
import FolderLogsShowDialog from "@/modules/Folder/Components/FolderLogsShowDialog.vue";
import FolderChangeOwnerDialog from "@/modules/Folder/Components/FolderChangeOwnerDialog.vue";
import FolderAssignCollaboratorDialog from "@/modules/Folder/Components/FolderAssignCollaboratorDialog.vue";
import FolderEditWorkflowDialog from "@/modules/Folder/Components/FolderEditWorkflowDialog.vue";
import FolderRecords from "@/modules/Folder/Components/FolderRecords.vue";
import FolderSendDataToDPAEDialog from "../Components/FolderSendDataToDPAEDialog.vue";
import FolderSendMailDialog from "../Components/FolderSendMailDialog.vue";
import FolderDeleteDialog from "../Components/FolderDeleteDialog.vue";
import FolderRelaunchDialog from "@/modules/Folder/Components/FolderRelaunchDialog.vue";
import FolderTagList from "../Components/FolderTag/FolderTagList.vue";
import FolderNotificationHistoryDialog from "@/modules/Folder/Components/FolderNotificationHistoryDialog.vue";
import { saveAs } from "file-saver";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";

export default {
  name: "FolderDetail",
  components: {
    FolderWokflowDetail,
    FolderEditNameDialog,
    FolderLogsShowDialog,
    FolderChangeOwnerDialog,
    FolderAssignCollaboratorDialog,
    FolderEditWorkflowDialog,
    FolderRecords,
    FolderSendDataToDPAEDialog,
    FolderSendMailDialog,
    FolderDeleteDialog,
    FolderAssignWatcherDialog,
    FolderRelaunchDialog,
    FolderTagList,
    FolderNotificationHistoryDialog,
  },
  mixins: [validationMixin],

  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
    folderId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      updatingFolderState: false,
      commentsCount: 0,
      fetchingWorkflow: true,
      submittingFollower: false,
      openAssignWatcherDialog: false,
      exportCheck: null,
      exportingType: null,
      displayRelaunchFolderDialog: false,
      displayNotificationHistoryDialog: false,
      folderReminds: [],
      commentDrawerAlreadyOpened: false,
      active: false,
      menuOpen: [],
      editSection: { id: null, content: null },
      comments: {
        items: [],
        loading: false,
        page: 1,
        pageSize: 10,
        next: null,
      },
      loadingMoreComments: false,
      isSendingComment: false,
      form: {
        comment: "",
      },
      nestedMenuModel: {
        first: false,
        second: false,
      },
      watchers: {
        items: [],
        loading: true,
      },
    };
  },
  validations: {
    form: {
      comment: {
        required,
      },
    },
  },
  computed: {
    ...mapState({
      user: (state) => state.account.user,
      folder: (state) => state.folder.activeFolder,
      activeWorkflow: (state) => state.workflow.activeWorkflow,
    }),
    workflowId() {
      return this.folder ? this.folder.workflow : null;
    },
    assignEmail() {
      return this.folder?.assign ? this.folder?.assign?.email : "";
    },
    getValidationColor() {
      switch (this.folder.validationStatus) {
        case "UPDATE_REQUESTED":
          return "#ff5267";
        case "UPDATED":
          return "#ffab5a";
        case "VALIDATED":
          return "#18a300";
        default:
          return this.folder.hasSpamEmail ? "#FF6B00" : "#b6bdff";
      }
    },
    hasClient() {
      return !this.folder?.client?.isTemporary;
    },
    userIsWatching() {
      return this.watchers.items.some((item) => item.id === this.user.id);
    },
    watchingBtn() {
      if (this.userIsWatching)
        return {
          icon: "$mdiEyeOff",
          label: this.$t("utils.unfollow"),
        };
      return {
        icon: "$mdiEye",
        label: this.$t("utils.follow"),
      };
    },
    lastNotification() {
      if (!this.folderReminds.length) return null;
      return this.folderReminds[0];
    },
    showDownloadZipBtn() {
      if (!this.exportCheck?.lastZipExport) return false;
      const now = new Date();
      const twoDaysInMs = 2 * 24 * 60 * 60 * 1000;
      const diffInMs = now - this.exportCheck.lastZipExport.dateCreated;
      return diffInMs <= twoDaysInMs;
    },
    disabledDownloadZipBtn() {
      return (
        !this.exportCheck?.lastZipExport ||
        this.exportCheck.lastZipExport.status !== EXPORT_STATUS.SUCCESS
      );
    },
    exportInError() {
      return (
        !this.exportCheck?.lastZipExport ||
        this.exportCheck.lastZipExport.status === EXPORT_STATUS.FAILED
      );
    },
    disabledExportZipBtn() {
      if (!this.exportCheck?.lastZipExport) return false;
      return (
        this.exportCheck.lastZipExport.status === EXPORT_STATUS.IN_PROGRESS
      );
    },
    enableRelaunch() {
      if (!this.activeWorkflow) return true;
      return this.activeWorkflow.sendCollectionEmails;
    },
    remainingDeletionDays() {
      if (!this.folder || isNaN(this.folder.remainingDeletionDays)) return "";
      if (this.folder.remainingDeletionDays == 0)
        return this.$t("workflow_delete_folder.folder_alert_2");
      if (this.folder.remainingDeletionDays > 0)
        return this.$tc(
          "workflow_delete_folder.folder_alert",
          this.folder.remainingDeletionDays,
          {
            days: this.folder.remainingDeletionDays,
          }
        );
      return "";
    },
  },
  watch: {
    folderId: {
      async handler(newValue, oldValue) {
        if (newValue != oldValue) {
          this.loadStoreData();
          this.commentDrawerAlreadyOpened = false;
          this.comments = {
            ...this.comments,
            items: [],
            loading: false,
            page: 1,
            next: null,
          };
          this.loadingMoreComments = false;
        }
      },
      immediate: true,
    },
    folder: {
      handler(newVal, oldVal) {
        if ((!oldVal && newVal) || (newVal && newVal.name !== oldVal.name)) {
          this.setBreadcrumbLabel();
        }
        this.commentsCount = this.folder?.commentsCount;
      },
      immediate: true,
    },
    workflowId: {
      handler(newValue, oldValue) {
        if (newValue != oldValue) {
          this.fetchWorkflow();
        }
      },
      immediate: true,
    },
  },
  created() {
    this.fetchFolderWatcher();
    this.fetchFolderExportCheck();
    this.fetchFolderReminds();
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  destroyed() {
    this.$store.commit("workflow/SET_ACTIVE_WORKFLOW", null);
    this.$store.commit("folder/SET_ACTIVE_FOLDER", null);
  },
  methods: {
    async fetchFolderReminds() {
      try {
        this.folderReminds = await getFolderRemind(
          this.organizationId,
          this.folderId
        );
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    onRelaunch() {
      this.fetchFolderReminds();
    },
    //comment logic
    handleClickOutside() {
      if (this.$refs.dropdown) {
        this.menuOpen = [];
      }
    },
    async fetchFolderComments({ reset = true } = {}) {
      if (reset) {
        this.comments.page = 1;
        this.comments.next = null;
        this.comments.loading = true;
      } else {
        this.loadingMoreComments = true;
      }
      try {
        const response = await getFolderComments({
          organizationId: this.organizationId,
          folderId: this.folderId,
          params: {
            page: this.comments.page,
            pageSize: this.comments.pageSize,
            page_size: this.comments.pageSize,
          },
        });
        const data = response?.data ?? [];
        const results = data?.results ?? data ?? [];
        const mappedComments = Array.isArray(results)
          ? results.map((item) => new Comment(item)).reverse()
          : [];

        if (reset) {
          this.comments.items = mappedComments;
        } else {
          const ids = new Set(this.comments.items.map((comment) => comment.id));
          const olderComments = mappedComments.filter(
            (comment) => !ids.has(comment.id)
          );
          this.comments.items = [...olderComments, ...this.comments.items];
        }

        this.comments.next = data?.next ?? null;
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.comments.loading = false;
      this.loadingMoreComments = false;
    },
    onCommentsScroll(event) {
      const container = event?.target;
      if (!container || this.comments.loading || this.loadingMoreComments)
        return;

      const threshold = 80;
      const hasReachedTop = container.scrollTop <= threshold;
      if (!hasReachedTop) return;

      this.loadMoreComments();
    },
    getNextCommentsPage() {
      if (!this.comments.next) return null;

      if (typeof this.comments.next === "number") {
        return this.comments.next;
      }

      if (typeof this.comments.next === "string") {
        const numericPage = Number(this.comments.next);
        if (!Number.isNaN(numericPage)) return numericPage;

        try {
          const parsedUrl = new URL(this.comments.next, window.location.origin);
          const page = Number(parsedUrl.searchParams.get("page"));
          return Number.isNaN(page) ? null : page;
        } catch {
          return null;
        }
      }

      return null;
    },
    async loadMoreComments() {
      const nextPage = this.getNextCommentsPage();
      if (!nextPage) return;

      const list = this.$refs.commentList;
      const previousScrollHeight = list?.scrollHeight ?? 0;
      const previousScrollTop = list?.scrollTop ?? 0;

      this.comments.page = nextPage;
      await this.fetchFolderComments({ reset: false });
      this.$nextTick(() => {
        const currentList = this.$refs.commentList;
        if (!currentList) return;

        const currentScrollHeight = currentList.scrollHeight;
        currentList.scrollTop =
          currentScrollHeight - previousScrollHeight + previousScrollTop;
      });
    },
    closeDrawer() {
      this.active = false;
      this.editSection = { id: null, content: null };
      this.menuOpen = [];
    },
    async sendComment() {
      const payload = {
        content: this.form.comment,
      };
      try {
        this.isSendingComment = true;
        const response = await createFolderComment({
          organizationId: this.organizationId,
          folderId: this.folderId,
          payload,
        });

        this.comments.items.push(new Comment(response.data));
        this.form.comment = "";
        this.$nextTick(() => {
          this.scrollToLatestComments();
          this.focusCommentEditor();
        });

        this.commentsCount += 1;
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.isSendingComment = false;
    },
    async editComment() {
      const payload = {
        content: this.editSection.content,
      };
      try {
        await updateFolderComment({
          organizationId: this.organizationId,
          folderId: this.folderId,
          commentId: this.editSection.id,
          payload,
        });
        this.comments.items.find(
          (comment) => comment.id === this.editSection.id
        ).content = this.editSection.content;
        this.editSection = { id: null, content: null };
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    async deleteFolderComment(commentId) {
      try {
        await deleteFolderComment({
          organizationId: this.organizationId,
          folderId: this.folderId,
          commentId,
        });
        this.comments.items = this.comments.items.filter(
          (comment) => comment.id !== commentId
        );
        if (this.folder && this.folder.commentsCount > 0) {
          this.folder.commentsCount -= 1;
        }
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    async openCommentSection() {
      this.active = true;
      if (!this.commentDrawerAlreadyOpened) {
        this.commentDrawerAlreadyOpened = true;
        await this.fetchFolderComments();
      }

      this.$nextTick(() => {
        this.moveToLatestComment();
        this.focusCommentEditor();
      });
    },
    moveToLatestComment({ behavior = "auto" } = {}) {
      this.scrollToLatestComments(behavior);
    },
    scrollToLatestComments(behavior = "auto") {
      const list = this.$refs.commentList;
      if (!list) return;

      list.scrollTo({
        top: list.scrollHeight,
        behavior,
      });
    },
    focusCommentEditor() {
      const editor = document.getElementById("comment-editor");
      if (!editor) return;

      const input = editor.querySelector(
        "textarea, [contenteditable='true'], input"
      );
      if (input) {
        input.focus();
        return;
      }

      if (typeof editor.focus === "function") {
        editor.focus();
      }
    },
    setMenuOpen(commentId) {
      if (this.menuOpen.length > 0) this.menuOpen = [];
      this.menuOpen.push(commentId);
    },
    async loadStoreData() {
      if (!this.organizationId || !this.folderId) return;
      try {
        await this.$store.dispatch("folder/fetchActiveFolder", {
          organizationId: this.organizationId,
          folderId: this.folderId,
        });
        await this.$store.dispatch("folder/fetchClientOwnfolder", {
          organizationId: this.organizationId,
          folderId: this.folderId,
        });
      } catch {
        this.$router.push({
          name: "folderList",
          params: {
            organizationId: this.organizationId,
          },
        });
      }
    },
    async fetchWorkflow() {
      this.fetchingWorkflow = true;
      this.$store.commit("workflow/SET_ACTIVE_WORKFLOW", null);
      if (this.workflowId) {
        await this.$store.dispatch("workflow/fetchActiveWorkflow", {
          organizationId: this.organizationId,
          workflowId: this.workflowId,
        });
      }
      this.fetchingWorkflow = false;
    },
    setBreadcrumbLabel() {
      this.$route.meta.breadcrumbs[1].label = `${this.folder.name} `;
    },
    async fetchFolderWatcher() {
      this.watchers.loading = true;
      try {
        const res = await FolderAPI.getFolderWatcher({
          organizationId: this.organizationId,
          folderId: this.folderId,
        });
        this.watchers.items = res.data.results.map(
          (item) => new User({ ...item })
        );
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.watchers.loading = false;
    },
    async handleFollow() {
      this.submittingFollower = true;
      try {
        const params = {
          organizationId: this.organizationId,
          folderId: this.folderId,
          payload: {
            collaborators: [this.user.id],
          },
        };
        if (this.userIsWatching) {
          await FolderAPI.postFolderWatcherUnfollow(params);
          this.watchers.items = this.watchers.items.filter(
            (item) => item.id !== this.user.id
          );
        } else {
          await FolderAPI.postFolderWatcherFollow(params);
          this.watchers.items.push(new User({ ...this.user }));
        }
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.submittingFollower = false;
    },
    updateWatchers(items) {
      this.watchers.items = items;
    },
    async fetchFolderExportCheck() {
      try {
        this.exportCheck = await getFolderExportCheck({
          organizationId: this.organizationId,
          folderId: this.folderId,
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },

    async downloadFolder(fileType) {
      this.exportingType = fileType;
      try {
        const folderExport = await postFolderExport({
          organizationId: this.$route.params.organizationId,
          folderId: this.$route.params.folderId,
          payload: {
            export_file_type: fileType,
          },
        });
        if (folderExport.exportFileType == EXPORT_FILE_TYPE.PDF) {
          saveAs(folderExport.exportFile);
        } else {
          this.exportCheck.lastZipExport = folderExport;
          this.$store.dispatch("snackbar/active", {
            message: this.$t("folder.downloadFolderInProgress"),
            type: "SUCCESS",
          });
        }
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.oopsError"),
          type: "ERROR",
        });
      }
      this.exportingType = null;
      this.nestedMenuModel = {
        first: false,
        second: false,
        third: false,
      };
    },
    downloadZIPFile(zipExported) {
      saveAs(zipExported.exportFile);
    },
    async archiveFolder() {
      this.updatingFolderState = true;
      try {
        await this.$store.dispatch("folder/archiveFolder", {
          organizationId: this.$route.params.organizationId,
          folderId: this.$store.state.folder.activeFolder.id,
        });
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.snackbar.folderArchivedSuccessfully"),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.updatingFolderState = false;
    },
    async unarchiveFolder() {
      this.updatingFolderState = true;
      try {
        await this.$store.dispatch("folder/unarchiveFolder", {
          organizationId: this.$route.params.organizationId,
          folderId: this.$store.state.folder.activeFolder.id,
        });
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.snackbar.folderUnarchivedSuccessfully"),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.updatingFolderState = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.folder_container {
  position: relative;
  height: 100%;
  .container_full {
    display: flex;
    gap: 10px;
    height: 100%;
    .main_section {
      flex: 1;
      .archived_alert {
        padding: 16px;
        border-radius: 5px;
        background: #f7e7da;
        display: flex;
        align-items: center;
        margin-bottom: 16px;
        position: sticky;
        top: -32px;
        z-index: 100;
        .archived_alert_icon {
          &::v-deep {
            .v-icon {
              color: #ff6b00;
            }
          }
        }
        .archived_alert_content {
          padding: 0 16px;
          .archived_info {
            display: flex;
            align-items: center;
            font-family: "Inter";
            font-style: normal;
            font-weight: 400;
            font-size: 12px;
            line-height: 18px;
            color: #ff6b00;
            .text_strong {
              font-weight: 600;
              margin-left: 6px;
            }
          }
        }
      }
      .folder_header {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        grid-gap: 24px;
        margin-bottom: 24px;
        .header_item {
          display: flex;
          flex-direction: column;
          border-radius: 5px;
          flex: 1;
          padding: 24px;
          background: #ffffff;
          border: 0.5px solid #ecebf1;
          position: relative;
          width: 100%;
          overflow: hidden;
          word-break: keep-all;
          word-wrap: break-word;
          .pastille {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            padding: 8px;
            margin-right: 10px;
            position: relative;
            top: 5px;
          }
          .item_title {
            display: flex;
            align-items: center;
            font-family: "Inter";
            font-style: normal;
            font-weight: 400;
            font-size: 12px;
            line-height: 16px;
            text-transform: uppercase;
            color: #707080;
            margin-bottom: 0.3em;
            .action {
              width: 24px;
              height: 24px;
              margin-left: auto;
            }
            .skeleton {
              height: 16px;
              width: 70px;
              ::v-deep {
                .v-skeleton-loader__bone {
                  height: 12px;
                }
              }
            }
          }
          .sub_title {
            font-family: "Inter";
            font-style: normal;
            font-weight: 600;
            font-size: 24px;
            line-height: 32px;
            color: #353542;
            margin-bottom: 0.6em;
            .skeleton {
              height: 32px;
              width: 66%;
              ::v-deep {
                .v-skeleton-loader__bone {
                  height: 24px;
                }
              }
            }
          }
          .item_content {
            display: flex;
            margin-top: 0.6em;
            .item_key {
              margin-right: 6px;
              font-family: "Inter";
              font-style: normal;
              font-weight: 400;
              font-size: 12px;
              line-height: 16px;
              color: #707080;
            }
            .item_value {
              font-family: "Inter";
              font-style: normal;
              font-weight: 600;
              font-size: 12px;
              line-height: 16px;
              color: #707080;
            }
            .skeleton {
              height: 16px;
              width: 50%;
              ::v-deep {
                .v-skeleton-loader__bone {
                  height: 12px;
                }
              }
            }
          }
          .alert_message {
            margin-top: 6px;
            font-size: 12px;
            line-height: 16px;
            color: red;
          }
          .item_footer_action {
            align-self: flex-end;
            justify-self: flex-end;
            padding-top: 12px;
            margin-top: auto;
            gap: 8px;
            display: flex;
            align-items: center;
            .footer_btn {
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 8px 12px;
              background: #f4f7ff;
              border-radius: 5px;
              font-family: "Inter";
              font-style: normal;
              font-weight: 500;
              font-size: 14px;
              line-height: 16px;
              color: #2536cc;
              & > .icon {
                &::v-deep {
                  .v-icon {
                    width: 16px;
                    height: 16px;
                    color: #2536cc;
                  }
                }
              }
              & > * {
                margin: 0 4px;
              }
            }
          }
          .relaunch_btn {
            margin-top: 16px;
          }
          .followers {
            display: flex;
            align-items: center;
            padding-left: 10px;
            cursor: pointer;
            .avatar_container {
              border: solid 1px #fff;
              margin-left: -10px;
              width: 32px;
              height: 32px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: #e5e5e5;
              border-radius: 50%;
              font-family: "Inter";
              font-size: 16px;
              line-height: 16px;
              color: #353542;
              font-weight: 600;
            }
            .dots {
              margin-left: 4px;
              font-weight: bold;
              line-height: 16px;
              font-size: 20px;
              align-self: flex-end;
              margin-bottom: 4px;
              color: #707080;
            }
          }
          .tag_list {
            margin-top: 6px;
          }
          .download_zip_btn {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 8px 12px;
            background: #f7e7da;
            border-radius: 5px;
            font-family: "Inter";
            font-style: normal;
            font-weight: 500;
            font-size: 14px;
            line-height: 16px;
            color: #ff6b00;
            gap: 4px;
            margin-top: 12px;
            & > .icon {
              &::v-deep {
                .v-icon {
                  width: 16px;
                  height: 16px;
                  color: #ff6b00;
                }
              }
            }
            &:disabled {
              background: #f4f7ff;
              color: #c5c5cf;
              cursor: not-allowed;
              & > .icon {
                &::v-deep {
                  .v-icon {
                    color: #c5c5cf;
                  }
                }
              }
            }
          }

          .item_content_right {
            display: flex;
            justify-content: flex-end;
            margin-top: 16px;
            .unarchive_btn {
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 8px 12px;
              background: #f7e7da;
              border-radius: 5px;
              font-family: "Inter";
              font-style: normal;
              font-weight: 500;
              font-size: 14px;
              line-height: 16px;
              color: #ff6b00;
              & > .icon {
                &::v-deep {
                  .v-icon {
                    width: 16px;
                    height: 16px;
                    color: #ff6b00;
                  }
                }
              }
              & > * {
                margin: 0 4px;
              }
            }
          }
        }
      }
      .completion_rate {
        margin-bottom: 24px;
      }
      .folder_body {
        .stepper_content {
          display: flex;
          .status_section {
            width: calc(100% - 250px);
            flex: none;
          }
          .action_section {
            width: 250px;
            flex: none;
          }
        }
      }
    }
    .comment_section {
      width: 50px;
      transition: 300ms ease-in-out;
      background: white;
      border-radius: 5px;
      background: #ffffff;
      border: 0.5px solid #ecebf1;

      .default_view {
        width: 50px;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 24px;
        cursor: pointer;
        background: #dadff8;
        .icon_container {
          position: relative;
          .comment_badge {
            position: absolute;
            top: -8px;
            right: -8px;
            background: #2536cc;
            color: #ffffff;
            border-radius: 50%;
            min-width: 18px;
            height: 18px;
            padding: 0 3px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: "Inter";
            font-size: 10px;
            font-weight: 600;
            line-height: 1;
          }
        }
      }
      .comment_views {
        display: flex;
        flex-direction: column;
        padding: 24px;
        max-width: 450px;

        .comment_title {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-family: "Inter";
          font-style: normal;
          font-weight: 600;
          font-size: 18px;
          line-height: 20px;
          color: #353542;
          margin-bottom: 16px;
          text-decoration: underline;
          .close_icon {
            transform: translate(10px, -10px);
            &::v-deep {
              .v-icon {
                color: #707080;
              }
            }
            cursor: pointer;
          }
        }
        .comment_list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          height: calc(100% - 320px);
          overflow: auto;

          .comment_item {
            display: flex;
            flex-direction: column;
            padding: 12px;
            border-radius: 5px;
            background: #f4f7ff;
            .header_item {
              display: flex;
              align-items: center;
              gap: 10px;
              margin-bottom: 8px;
              .name_container {
                width: 100%;
                .author_name {
                  font-family: "Inter";
                  font-style: normal;
                  font-weight: 500;
                  font-size: 14px;
                  line-height: 18px;
                  color: #2536cc;
                  position: relative;
                  .dropdown {
                    z-index: 100;
                    position: absolute;
                    top: 27px;
                    right: 0;
                    width: 150px;
                    padding: 8px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: start;
                    gap: 8px;
                    border-radius: 5px;
                    background: #fff;
                    box-shadow: 0px 6px 10px 0px rgba(46, 45, 193, 0.06),
                      0px 2px 4px 0px rgba(46, 45, 193, 0.1);

                    .dropdown_btn {
                      display: flex;
                      justify-content: flex-start;
                      align-items: center;
                      width: 100%;
                      height: 45px;
                      background-color: #ffffff !important;
                      font-size: 14px !important;
                      font-family: "Inter";
                      font-style: normal;
                      font-weight: 400 !important;
                      line-height: 16px;
                      color: #707080;
                      .drop-menu {
                        margin: 0 6px;
                      }

                      &:hover {
                        color: #2536cc;
                        background: #f4f8ff !important;
                        border-radius: 5px;
                        cursor: pointer;
                        ::v-deep .v-icon__svg {
                          color: #2536cc;
                        }
                      }

                      .icon {
                        ::v-deep {
                          .v-icon__svg {
                            width: 18px;
                            height: 18px;

                            :hover {
                              color: #2536cc !important;
                            }
                          }
                        }
                      }

                      & > * {
                        margin: 0 6px;
                      }

                      &:disabled {
                        & > .icon {
                          &::v-deep {
                            .v-icon {
                              color: #707080;
                            }
                          }
                        }

                        color: #707080;
                      }
                    }
                  }
                }
              }

              .date_created {
                font-weight: 500;
                font-size: 12px;
                color: #707080;
              }
            }
            .comment {
              font-weight: 600;
              font-size: 12px;
              color: #353542;
            }
          }
        }
      }

      &.active {
        display: inline-flex;
        width: auto;
      }
    }
  }
}
.menu_list {
  background: #fff;
  .menu_list_item {
    display: flex;
    justify-content: flex-start;
    &::v-deep {
      .icon {
        color: #cfcfdc;
      }
      .label {
        font-family: "Inter";
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 16px;
        color: #707080;
        transition: all 0.3s ease-in;
      }
    }
    &:hover {
      &::v-deep {
        .icon {
          color: #2536cc;
        }
        .label {
          color: #2536cc;
        }
      }
    }
    &.v-btn--disabled {
      &::v-deep {
        .icon {
          .v-icon {
            color: #cfcfdc !important;
          }
        }
        .label {
          color: #cfcfdc;
        }
      }
    }
  }
}
.custom_tooltip {
  position: relative;
  .tooltip_content {
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(0, calc(-100% - 20px));
    padding: 12px 16px;
    border-radius: 5px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.25s;
    background-color: #707080;
    z-index: 10;
    width: 200px;
    font-size: 14px;
    line-height: 18px;
    color: #fff;
    text-align: center;
  }
  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(-24px, -21px);
    height: 0;
    width: 0;
    border: 12px solid transparent;
    border-top-color: #707080;
    z-index: 11;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.25s;
  }

  &:hover {
    .tooltip_content {
      opacity: 1;
    }
    &::before {
      opacity: 1;
    }
  }
}
.custom_tooltip {
  position: relative;
  .tooltip_content {
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(0, calc(-100% - 20px));
    padding: 12px 16px;
    border-radius: 5px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.25s;
    background-color: #707080;
    z-index: 10;
    width: 200px;
    font-size: 14px;
    line-height: 18px;
    color: #fff;
    text-align: center;
  }
  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(-24px, -21px);
    height: 0;
    width: 0;
    border: 12px solid transparent;
    border-top-color: #707080;
    z-index: 11;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.25s;
  }

  &:hover {
    .tooltip_content {
      opacity: 1;
    }
    &::before {
      opacity: 1;
    }
  }
}
.action_container {
  display: flex;
  gap: 12px;
  margin-top: 12px;
  .btn_1 {
    flex: 1;
  }
  .btn_2 {
    width: fit-content;
    & > .icon {
      &::v-deep {
        .v-icon {
          width: 16px;
          height: 16px;
          color: #707080;
        }
      }
    }
  }
}
</style>
